import { useCallback, useEffect, useRef, useState } from 'react';

import { lockBodyScroll, unlockBodyScroll } from './body-scroll-lock';
import { UseDialogProps } from './use-dialog.types';

export const useDialog = ({ closeOnOutsideClick = true }: UseDialogProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasScrollLock = useRef(false);

  /**
   * The lock is shared and counted, so each dialog must claim and release it at
   * most once — these guards keep this hook's contribution to that count at 0/1
   * however the dialog was opened or closed (action, Esc, backdrop, unmount).
   */
  const acquireScrollLock = useCallback(() => {
    if (hasScrollLock.current) return;
    hasScrollLock.current = true;
    lockBodyScroll();
  }, []);

  const releaseScrollLock = useCallback(() => {
    if (!hasScrollLock.current) return;
    hasScrollLock.current = false;
    unlockBodyScroll();
  }, []);

  const closeDialog = useCallback(() => {
    dialogRef.current?.close();
    releaseScrollLock();
    setIsOpen(false);
  }, [releaseScrollLock]);

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      const click = e.target;
      if (click === dialogRef.current) {
        closeDialog();
      }
    },
    [closeDialog],
  );

  /**
   * Native `close` fires for Esc and any programmatic close. Listening to it
   * keeps isOpen in sync even when our own actions weren't the trigger.
   */
  const handleNativeClose = useCallback(() => {
    releaseScrollLock();
    setIsOpen(false);
  }, [releaseScrollLock]);

  /**
   * Bind listeners to the current dialog node. Idempotent: the DOM ignores a
   * re-add of an identical (type, listener) pair, so this is safe to call from
   * both the mount effect (dialog present on first render) and the open actions
   * (dialog mounted conditionally / after the first render — the race this hook
   * previously got wrong, where listeners never bound and isOpen never updated).
   */
  const bindListeners = useCallback(() => {
    const node = dialogRef.current;
    if (!node) return;
    node.addEventListener('close', handleNativeClose);
    if (closeOnOutsideClick) {
      node.addEventListener('click', handleOutsideClick);
    }
  }, [handleNativeClose, handleOutsideClick, closeOnOutsideClick]);

  /**
   * Open the dialog as a modal dialog element.
   * Inlcudes a backdrop that prevents interaction with the rest of the page.
   * Clicking outside the dialog will close it.
   */
  const openModalDialog = useCallback(() => {
    if (!dialogRef.current) return;
    bindListeners();
    dialogRef.current?.showModal();
    acquireScrollLock();
    setIsOpen(true);
  }, [bindListeners, acquireScrollLock]);

  /**
   * Open the dialog as a modeless dialog element.
   * Does not include a backdrop that prevents interaction with the rest of the page.
   * Clicking outside the dialog will not close
   * Note: According to Dawn UI guidelines, modeless dialogs should not be needed. Use openModalDialog instead.
   */
  const openDialog = useCallback(() => {
    if (!dialogRef.current) return;
    bindListeners();
    dialogRef.current?.show();
    setIsOpen(true);
  }, [bindListeners]);

  const toggleDialog = () => {
    if (dialogRef.current?.open) {
      closeDialog();
      return;
    }
    openModalDialog();
  };

  // Common case: the dialog is present on the hook's first render. Bind and sync
  // initial state here; late-mounted dialogs are handled by the open actions.
  useEffect(() => {
    bindListeners();
    const node = dialogRef.current;
    if (node) {
      setIsOpen(node.open);
    }
    return () => {
      if (!node) return;
      node.removeEventListener('close', handleNativeClose);
      node.removeEventListener('click', handleOutsideClick);
    };
  }, [bindListeners, handleNativeClose, handleOutsideClick]);

  // Unmounting while open removes the dialog without firing `close`, which would
  // otherwise strand the body scroll lock.
  useEffect(() => releaseScrollLock, [releaseScrollLock]);

  return {
    openModalDialog,
    openDialog,
    closeDialog,
    isOpen,
    toggleDialog,
    dialogRef,
  };
};
